/**
 * Summer50 user-facing copy, keyed by copy-pack ID (docs/plans/FOUNDING_50_COPY_PACK.md v3).
 * Centralized so Paul's edits map 1:1 and the FE dev tweaks copy in one place.
 */

import { freeMonthEndedStillApplies } from '@/utils/sevenFreeDaysCopy';
import { freePeriodName, isFreeMonth, normalizeFreePeriodDays } from '@/utils/freePeriodNaming';

/** "first week" / "first 2 weeks" / "first month" / "first N days" (G, #4119 review L8). */
function firstPeriodPhrase(days?: number | null): string {
  const n = normalizeFreePeriodDays(days);
  if (n === 7) return 'first week';
  if (n === 14) return 'first 2 weeks';
  if (isFreeMonth(n)) return 'first month';
  return `first ${n} days`;
}

/** "your free week ends" / "your 2 free weeks end" (G, #4119 review L8). */
function periodEndsPhrase(days?: number | null): string {
  const name = freePeriodName(normalizeFreePeriodDays(days));
  return `your ${name} ${/s$/.test(name) ? 'end' : 'ends'}`;
}

export const S50 = {
  // B — code discovery & entry
  B1_label: 'Redeem code',
  B1_sub: "Have an invite or friend's code? Use it here.",
  B2_callout: "Redeem code — have an invite or friend's code? It only takes a second.",
  B3_paywall_link: 'Redeem code',
  B4_title: 'Redeem code',
  B4_sub: 'Invite and reward codes go here.',
  B5_placeholder: 'Enter your code',
  B6_submit: 'Redeem code',
  // C — success
  C1_headline: 'Your month of Coach Cedar is free.',
  C1_line2: 'Make the most of it.',
  C1_body: (endDate: string) => `Full access. No card, nothing to cancel. Free until ${endDate}.`,
  C1_reward: 'Invite a friend and get 50% off your first month when your free month ends.',
  C1_fine: 'Your free month replaces the standard trial.',
  C1_cta: 'Meet your coach',
  C1d_deferred: (date: string) => `Your free month starts when your trial ends on ${date} — turn off auto-renew in your App Store settings so you're not charged in between.`,
  // C1j (journey ruling J21, 2026-09-27): a code redeemed during the free period makes the
  // whole free period a free month from its start — the month includes the days used.
  C1j_joined: (date: string) => `Your free trial is now a free month. It runs until ${date}. No card, nothing to cancel.`,
  C2_headline: 'Welcome!',
  C2_body: (days?: number | null) =>
    `Chat with Coach Cedar to build your first plan together. Coach Cedar is all yours for your ${firstPeriodPhrase(days)} — make the most of it!`,
  C2_beat: (referrer: string) => `${referrer} gets credit when you start your trial — and it works both ways: refer a friend and you get 50% off a month of Chat+.`,
  C2_cta: "Let's go",
  C2_secondary: 'See how referrals work',
  C3_toast_code: 'Code applied',
  C4_toast_reward: 'Reward applied',
  C5_cap_warn: 'Heads up — 20 Coach Cedar responses left this week on your free month.',
  C6_cap_block: "You've hit this week's message limit on your free month. It resets over the next few days — or message us in Support if you're training hard and need more.",
  // D — referral drawer
  D1_label: 'Train with a friend',
  D1_sub_default: 'Give a free week, earn 50% off Chat+',
  D1_sub_reward: (n: number) => `${n} reward${n === 1 ? '' : 's'} ready to claim`,
  D2_title: 'Train with a friend',
  D2_sub: 'They get their first week free. You get rewarded.',
  D3_code_label: 'Your code',
  D4_share: 'Share with a friend',
  D5_share_msg: (code: string, storeLink: string) =>
    `I train with Coach Cedar — it builds plans around my fitness goals and adapts with research-backed guidance. Your first week's free. If you try it, use my code ${code} — I get 50% off my subscription, and once you're in, you can do the same with a friend: ${storeLink}`,
  D6_how: 'How it works',
  D7_rung1: 'Friend 1 — 50% off a month of Chat+',
  // Currency-neutral: 50% off Chat+ is below the Essentials price in USD but NOT
  // in every market (CAD Chat+ ≈ $34.99 → $17.50 vs Essentials $16.99), so the
  // old "costs less than Essentials" claim shipped false to Canadian users.
  D7_rung1_detail: 'For that month you get everything in Chat+ for half the usual price.',
  D8_rung2: 'Friend 2 — 50% off another month',
  D9_rung3: 'Friend 3 — a year of Chat+ at 35% off the monthly rate',
  // Percentages hold in every market (USD 25%/35%, CAD 25%/36%); the dollar
  // figures did not — the renewal price now comes live from RevenueCat.
  D9_rung3_math: (annualPrice: string) =>
    `Annual already saves you 25% vs paying monthly. Your reward takes it to 35% off for your first year — then it renews at ${annualPrice}/year.`,
  D10_locked: (n: number) => `Locked — ${n} more friend${n === 1 ? '' : 's'} to go`,
  D11_pending: (name: string) => `${name} joined — unlocks when they start their trial`,
  D12_ready: 'Ready to claim',
  D12_days: (d: number) => `Claim within ${d} day${d === 1 ? '' : 's'}`,
  D13_claimed: (date: string) => `Claimed — applies to your ${date} bill`,
  // D12x — a reward that lapsed unclaimed. Was a bare hardcoded 'Expired' with
  // no explanation and no recourse.
  D12_expired: 'Expired — this one lapsed after 30 days. Message us in Support and we’ll take a look.',
  // D13r — an iOS reward whose offer code was issued but whose App Store sheet
  // was dismissed. The code is still valid; this reopens it.
  D13_reopen: 'Open redemption sheet',
  D14_privacy: 'Your friend will see your first name and when your trial starts.',
  D15_terms: 'Program terms',
  // D16 — Android claim posture (Apple offer codes only for now; Play fast-follow).
  // DRAFT (Paul review) — plan literal promoted to a copy ID.
  D16_android_claim: 'Claim on your iPhone for now',
  // E — inventory
  E1_section: 'Your rewards',
  // E2/E3 — "Your rewards" rows in SubscriptionDrawer.
  // DRAFT (Paul review — copy-pack v3 IDs not yet delivered).
  E2_ready_title: 'Reward ready to claim',
  E2_ready_sub: (n: number) => `${n} reward${n === 1 ? '' : 's'} waiting — 50% off a month of Chat+.`,
  E3_claimed_title: 'Claimed',
  E3_claimed_sub: (date: string) => `Applied to your ${date} bill.`,
  E3_earned_empty: 'Refer a friend to earn your first reward.',
  E5_oneliner: (date: string) => `Free month — ends ${date}`,
  E6_row_label: 'Train with a friend',
  E6_row_sub: 'Give a free week, earn 50% off Chat+',
  // G — month-end
  // G2 — dead-chat strip when the free month has ended.
  // G4/G5 — paywall context card (title + body) for an ended free month.
  // DRAFT (Paul review — copy-pack v3 IDs not yet delivered; G3 shipped).
  G2_deadchat_strip: 'Your free month is over — pick up any time to keep training with Coach Cedar.',
  G3_oneliner: 'Your free month is over — pick up any time',
  G4_paywall_title: 'Your free access has ended',
  G5_paywall_body: 'Pick up right where you left off — your plan, history, and coach are all still here. Choose a plan whenever you’re ready.',
  // H — claim
  // Platform-aware: iOS redeems an App Store offer code, Android purchases a
  // discounted Play offer. Naming the wrong store is a trust leak.
  H1_preflight: (isAndroid: boolean) =>
    isAndroid
      ? "One tap — Google Play confirms your discount and you're right back."
      : "One tap — Apple confirms your discount and you're right back.",
  H2_essentials_title: 'This reward is for Chat+',
  // Price comes live from RevenueCat (localized). The old copy hardcoded $12.49
  // and asserted "less than you pay now", which is false outside the US.
  H2_essentials_body: (chatPlusPrice: string) =>
    `Claiming upgrades you to Chat+ for one month at 50% off. After that month it renews at ${chatPlusPrice} unless you change your plan.`,
  H2_essentials_claim: 'Claim the upgrade',
  H2_essentials_later: 'Save it for later',
  H5_not_confirmed: (isAndroid: boolean) =>
    isAndroid
      ? "Hmm — Google Play didn't confirm that one. Your reward is still saved; try again in a minute or message us in Support."
      : "Hmm — Apple didn't confirm that one. Your reward is still saved; try again in a minute or message us in Support.",
  H6_restocking: "We're restocking reward codes — yours is safe and will be claimable shortly.",
  // H7 — store sheet dismissed. Nothing was charged and nothing was consumed;
  // say so instead of silently reverting the row.
  H7_cancelled: 'No charge — your reward is still saved.',
  // H8 — Play purchase went through but RevenueCat hasn't surfaced it yet.
  // Confirm-only retry (re-purchasing would be rejected as already-owned).
  H8_confirm_retry: 'Confirm purchase',
  // H3 — inline note shown on the claim CTA while the user's trial is still active.
  // DRAFT (Paul review — copy-pack v3 ID not yet delivered).
  // J22 (approved 2026-09-27, R1 applied).
  H3_trial_active: (days?: number | null) =>
    `Your reward is saved. Claim it once ${periodEndsPhrase(days)} and you continue with Coach Cedar.`,
  // H4 — inline note while the user's FREE MONTH is active: an Apple-"new" claimant
  // starts the paid Chat+ subscription the moment the offer code redeems, forfeiting
  // remaining free days. DRAFT (Paul review — added 2026-07-13 with the
  // eligibility-all-cohorts decision).
  H4_free_month_active: 'Heads up — claiming starts your paid Chat+ month right away. Your reward keeps for 30 days, so you can wait until your free month wraps.',
  // H2p — a Pro user claiming a Chat+ reward moves DOWN a tier for that period;
  // confirm before opening Apple's sheet (mirror of the Essentials H2 sheet).
  // DRAFT (Paul review — added 2026-07-13).
  H2_pro_title: 'This reward is for Chat+',
  H2_pro_body: "You're on CC Pro. Claiming applies this reward to Chat+ — a step down from Pro while it's active. Your reward keeps for 30 days if you'd rather stay on Pro.",
  H2_pro_claim: 'Claim on Chat+',
  H2_pro_later: 'Keep Pro for now',
  // H2f — FREE-ACCESS claimer. The free month rides tier='extended' under the
  // hood, so without this branch they fell into the Pro sheet and were told
  // "You're on CC Pro" — a tier they've never seen and aren't paying for.
  // Mirrors the never-show-tier-names-to-free-users invariant the paywall gate
  // test enforces.
  H2_free_title: 'This reward is for Chat+',
  H2_free_body: (daysLeft: number) =>
    `You're on free access right now, with ${daysLeft} day${daysLeft === 1 ? '' : 's'} still to go. Claiming starts a paid Chat+ month immediately and those free days go unused. Your reward keeps for 30 days.`,
  H2_free_claim: 'Claim anyway',
  H2_free_later: 'Keep my free access',
  // H2n — claimer with NO active subscription or trial (pre-trial, or lapsed).
  // Offer codes carry "not eligible for the intro offer", so redeeming starts a
  // PAID month with no free trial. This is the largest consequence in the flow
  // and previously had no confirm at all.
  H2_notrial_title: 'This starts a paid month',
  H2_notrial_body: (chatPlusPrice: string) =>
    `You don't have an active plan, so claiming starts a paid Chat+ month right away at 50% off — there's no free trial attached. It renews at ${chatPlusPrice} after. Your reward keeps for 30 days.`,
  H2_notrial_claim: 'Start paid month',
  H2_notrial_later: 'Not yet',
  // J — Program terms sheet body.
  // DRAFT (Paul review — copy-pack v3 "copy J" body not yet delivered).
  J_terms_title: 'Program terms',
  J_terms_body: [
    'Summer50 is a limited-time promotion from Coach Cedar.',
    '',
    'Free month: Redeeming a valid invite code grants one month of Chat+ at no charge. No payment method is required and there is nothing to cancel. Your free month replaces the standard 7-day trial. When it ends, you can choose a plan at any time.',
    '',
    'Referrals: Share your personal code with friends. When a friend redeems it and starts their trial, you earn a reward. Rewards unlock in order — refer 1 friend for 50% off a month of Chat+, a 2nd for another 50%-off month, and a 3rd for a year of Chat+ at a discounted rate.',
    '',
    'Rewards are delivered as App Store offer codes on iPhone, and as a discounted in-app offer on Android. A reward applies to your next eligible Chat+ bill and expires if not claimed within the stated window. Rewards have no cash value and cannot be combined, sold, or transferred.',
    '',
    'Privacy: A friend who redeems your code sees only your first name and when your trial starts — nothing else.',
    '',
    'Coach Cedar may change or end this program at any time. Abuse or fraudulent referrals may result in forfeited rewards.',
  ].join('\n'),
  // ── FA — Free Access presentation (mock approved 2026-07-16). The tier under
  // the hood is 'extended'; users see "Free Access" + a duration pill derived
  // from the grant dates (freeAccessDuration.ts) so any campaign length works.
  FA_paywall_title: 'Your Free Access',
  FA_section: 'Your current access',
  FA_card_name: 'Free Access',
  FA_line: (date: string) => `Free through ${date} · no charge · nothing to cancel`,
  FA_bullet1: 'Coach Cedar whenever you need — plans, chat, check-ins',
  FA_bullet2: 'Everything in CC Chat+ included',
  FA_plans_toggle: 'See plans for after your free access',
  FA_fine: (date: string) => `When your free access ends on ${date}, Coach Cedar will no longer be available to chat and plan until you select a paid plan — however, you'll always have access to your data.`,
  FA_deferred_ribbon: (pill: string, date: string) => `FREE ACCESS · ${pill} — starts when your trial ends (${date})`,
  FA_chip: 'FREE ACCESS',
  FA_days_left: (n: number) => `${n} day${n === 1 ? '' : 's'} left`,
  FA_day_of: (d: number, t: number) => `Day ${d} of ${t}`,
  // FA plans list (mock approved 2026-07-16): neutral options, informational
  // only — purchase unlocks when access ends (Apple has no future-start
  // purchase; buying early burns gift days or attaches the intro trial).
  FA_plans_desc_basic: 'Daily check-ins, workout debriefs & weekly planning',
  FA_plans_desc_full: 'Everything you have now — chat with Coach Cedar anytime',
  FA_plans_desc_extended: 'Unlimited chat — no weekly limits',
  FA_plans_level_tag: 'YOUR FREE ACCESS LEVEL',
  FA_plans_foot: (date: string) => `Plans start when your free access ends on ${date} — nothing to do today. We'll check in before then.`,
  // errors (from redeem endpoint reasons; endpoint returns the message, these are fallbacks)
  err_generic: "That code doesn't look right — double-check it and try again.",
};

/**
 * STRATEGIC — copy for hand-issued codes (evaluators, influencers).
 *
 * Only the strings that would be WRONG for this audience are overridden; everything
 * else falls through to S50 above. Two rules, both gate-enforced by
 * tests/gate/test_strategic_codes.py:
 *
 *   1. Never name the Summer50 campaign. A strategic recipient was never part of it;
 *      seeing it reads as a mistake at best and a mass-mailer at worst. (Founder
 *      directive 2026-07-31: "I don't want someone redeeming this code and seeing
 *      Summer50.")
 *   2. Never hard-code "month". A strategic grant can be a week or a year, so the
 *      length is always interpolated from the server's duration_label — the same
 *      pattern the FA_* family already uses successfully.
 *
 * Pick with `copyFor(family)` below rather than reaching in directly, so a missing
 * override can't silently fall back to campaign wording.
 */
export const STRATEGIC = {
  // Plain-string override so `copyFor()` stays type-compatible with S50 (whose
  // C1_headline is a string). It also doubles as the safe fallback for the rare case
  // where the server didn't send a duration_label — neutral, never campaign wording.
  C1_headline: 'Coach Cedar is on us.',
  // Preferred headline when the length IS known. Called explicitly by the success card.
  C1_headline_with_duration: (durationLabel: string) => `${durationLabel} of Coach Cedar, on us.`,
  C1_body: (endDate: string) => `Full access. No card, nothing to cancel. Free until ${endDate}.`,
  // Keeps the referral offer (genuinely useful to an influencer) but drops the
  // "when your free month ends" clause that assumes a one-month grant.
  C1_reward: 'Invite a friend and get 50% off a month of Chat+.',
  C1_fine: 'Full access to everything in Chat+ — nothing to cancel.',
  C1d_deferred: (date: string) =>
    `Your access starts when your trial ends on ${date} — turn off auto-renew in your App Store settings so you're not charged in between.`,
  // J21 for a strategic grant: never "month" (its length varies) — the dates say it.
  C1j_joined: (date: string) =>
    `Your free trial now includes your complimentary access. It runs until ${date}. No card, nothing to cancel.`,
  G2_deadchat_strip: 'Your access has ended — pick up any time to keep training with Coach Cedar.',
  G3_oneliner: 'Your access has ended — pick up any time',
  H4_free_month_active:
    'Heads up — claiming starts your paid Chat+ month right away. Your reward keeps for 30 days, so you can wait until your current access wraps.',
  J_terms_body: [
    'Coach Cedar complimentary access.',
    '',
    'Access: Redeeming a valid invite code grants full Chat+ access at no charge for the period shown when you redeemed. No payment method is required and there is nothing to cancel. When it ends, you can choose a plan at any time.',
    '',
    'Referrals: Share your personal code with friends. When a friend redeems it and starts their trial, you earn a reward. Rewards unlock in order — refer 1 friend for 50% off a month of Chat+, a 2nd for another 50%-off month, and a 3rd for a year of Chat+ at a discounted rate.',
    '',
    'Rewards are delivered as App Store offer codes on iPhone, and as a discounted in-app offer on Android. A reward applies to your next eligible Chat+ bill and expires if not claimed within the stated window. Rewards have no cash value and cannot be combined, sold, or transferred.',
    '',
    'Privacy: A friend who redeems your code sees only your first name and when your trial starts — nothing else.',
    '',
    'Coach Cedar may change or end this program at any time. Abuse or fraudulent referrals may result in forfeited rewards.',
  ].join('\n'),
};

/** The grant family a user's free access came from. Server-supplied. */
export type GrantFamily = 'summer50' | 'strategic' | null | undefined;

/**
 * Copy for a grant family: STRATEGIC overrides merged over S50.
 *
 * Anything the strategic block doesn't override falls through to the campaign copy,
 * which is correct — most of the pack (the whole FA_* family, the referral hub, the
 * reward-claim sheets) is already duration- and campaign-neutral.
 */
export function copyFor(family: GrantFamily) {
  return family === 'strategic' ? { ...S50, ...STRATEGIC } : S50;
}

/** The free-month fields every surface reads (useSubscription `freeMonth`). */
export interface FreeMonthState {
  active: boolean;
  closed_reason: string | null;
  family?: GrantFamily;
}

/**
 * An ENDED free month (ended on its date, or revoked) — the test Account's
 * one-liner has always used (SettingsScreen getSubscriptionSurface).
 */
export function isFreeMonthEnded(freeMonth: FreeMonthState | null | undefined): boolean {
  return !!freeMonth && !freeMonth.active
    && (freeMonth.closed_reason === 'free_month_ended' || freeMonth.closed_reason === 'free_month_revoked');
}

/**
 * The month-end card still names this user's state (follow-up to #3967): the free
 * month ended (or was revoked) AND nothing came after it that it would hide — not a
 * trial or a paid state now, and not a paid subscription that has since lapsed (a
 * lapsed payer reads the re-subscribe card). One test for every surface that picks
 * the month-end wording (the Subscription drawer's expired card).
 */
export function freeMonthEndedCardApplies(
  freeMonth: FreeMonthState | null | undefined,
  cohort: string | null | undefined,
  lapsedPaid: boolean | undefined,
): boolean {
  return isFreeMonthEnded(freeMonth) && freeMonthEndedStillApplies(cohort, lapsedPaid);
}

/**
 * The Subscription drawer's expired card for an ended free month (2026-09-26).
 *
 * A free-month user never had a trial, so "Trial Expired" / "Your 7 days are
 * done" misdescribes them. The card takes the month-end wording the other
 * surfaces already show — the chat strip's sentence (G2) as the message, the
 * month-end title (G4) as the heading — and keeps the expired card's own badge
 * and button. No new words.
 */
export function freeMonthEndedDrawerCopy<T extends { planName: string; message: string }>(
  base: T,
  family: GrantFamily,
): T {
  const c = copyFor(family);
  return { ...base, planName: c.G4_paywall_title, message: c.G2_deadchat_strip };
}
